import {
  Box,
  Button,
  CircularProgress,
  Link,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import lodash from "lodash";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getClientData } from "src/clients/ClientService";
import { selectUser } from "src/redux/slices/userSlice";
import { ClientData, UserDetails } from "src/types/ClientDetails";
import { useClientDataContext } from "../context/Context";
import Pagination from "../templates/Pagination";
import CreateUser from "./CreateUser";
import listOfUsersStyles from "./ListOfUsers.module.css";

interface listOfUsersProps {
  clientCode: string;
  handleOnPopUpSave: () => Promise<ClientData>;
}

const ListOfUsers = ({ clientCode, handleOnPopUpSave }: listOfUsersProps) => {
  const [initialUserList, setInitialUserList] = useState<UserDetails[]>([]);
  const { clientData } = useClientDataContext();
  const [selectedRow, setSelectedRow] = useState<number>(0);
  const [listOfUsers, setListOfUsers] = useState<UserDetails[]>(
    clientData.userDetailsList
  );
  const [actionBoolean, setActionBoolean] = useState<boolean>(false);
  const [mode, setMode] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);
  const user = useSelector(selectUser);
  const isUserEdit = user.permissionsList.includes("EDIT USER");

  useEffect(() => {
    setListOfUsers(clientData.userDetailsList);
    if (clientData.userDetailsList.length > 0) {
      setCurrentPage(1);
    }
  }, [clientData.userDetailsList]);

  useEffect(() => {
    fetchClient();
  }, []);

  const fetchClient = () => {
    getClientData(user.userId, user.password, clientCode).then(
      async (clientRes) => {
        if (clientRes.ok) {
          const clientResData = await clientRes.json();
          setInitialUserList([...clientResData.userDetailsList]);
        }
      }
    );
  };

  const handleRowSelect = (id: number) => {
    if (selectedRow !== id) {
      setSelectedRow(id);
    } else {
      setSelectedRow(0);
    }
  };

  const handleItemsPerPageChange = (event: any) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const compareUserLists = (
    initialList: UserDetails[],
    updatedList: UserDetails[]
  ) => {
    if (initialList.length !== updatedList.length) {
      return false;
    }

    for (let i = 0; i < initialList.length; i++) {
      if (!lodash.isEqual(initialList[i], updatedList[i])) {
        return false;
      }
    }

    return true;
  };

  const handleOnSaveAllUsers = () => {
    handleOnPopUpSave().then((res) => {
      setInitialUserList(res.userDetailsList);
    });
  };

  const handleOnSuccessBack = () => {
    fetchClient();
    setListOfUsers(clientData.userDetailsList);
    handleOnCreateUserCancel();
  };

  const handleOnCreateUserCancel = () => {
    setActionBoolean(false);
    setMode("");
    setSelectedRow(0);
  };

  const lastIndex = currentPage * itemsPerPage;
  const firstIndex = lastIndex - itemsPerPage;
  const currentPageItems = listOfUsers?.slice(firstIndex, lastIndex);

  if (!listOfUsers) {
    return (
      <Box className="loaderIconStylings">
        <CircularProgress disableShrink />
      </Box>
    );
  }

  return (
    <>
      {!actionBoolean && (
        <Box>
          <Box className={listOfUsersStyles.mainContainer}>
            <Box height="60vh">
              <Typography
                variant="h5"
                sx={{ padding: "2%" }}
              >
                List Of users
              </Typography>
              <TableContainer
                sx={{
                  padding: "0rem 2rem",
                  maxHeight: "46vh",
                  position: "sticky",
                }}
                className="tableBody"
              >
                <Table
                  stickyHeader
                  aria-label="sticky table"
                >
                  <TableHead className="table-row-odd">
                    <TableRow>
                      <TableCell className="tableHeader">Select</TableCell>
                      <TableCell className="tableHeader">Full Name</TableCell>
                      <TableCell className="tableHeader">User Id</TableCell>
                      <TableCell className="tableHeader">Role</TableCell>
                      <TableCell className="tableHeader">
                        Default Product
                      </TableCell>
                      <TableCell className="tableHeader">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentPageItems?.length === 0 ? (
                      <TableRow sx={{ height: "40vh" }}>
                        <TableCell
                          align="center"
                          colSpan={6}
                        >
                          No Users
                        </TableCell>
                      </TableRow>
                    ) : (
                      currentPageItems?.map(
                        (currUser: UserDetails, index: number) => (
                          <TableRow
                            key={index}
                            className={
                              index % 2 === 0
                                ? "table-row-even"
                                : "table-row-odd"
                            }
                          >
                            <TableCell>
                              <Radio
                                name="selectedUser"
                                checked={
                                  selectedRow ===
                                  (currentPage - 1) * itemsPerPage + index + 1
                                }
                                onClick={() =>
                                  handleRowSelect(
                                    (currentPage - 1) * itemsPerPage + index + 1
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {currUser.firstName} {currUser.lastName}
                            </TableCell>
                            <TableCell>
                              <Link
                                onClick={() => {
                                  setSelectedRow(
                                    (currentPage - 1) * itemsPerPage + index + 1
                                  );
                                  setActionBoolean(true);
                                  setMode(!isUserEdit ? "View" : "Edit");
                                }}
                              >
                                {currUser.userId}
                              </Link>
                            </TableCell>
                            <TableCell>{currUser.role.roleName}</TableCell>
                            <TableCell>{currUser.defaultProduct}</TableCell>
                            <TableCell>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                {currUser.status}
                                {!currUser.id && (
                                  <Box
                                    sx={{
                                      marginLeft: "4px",
                                      backgroundColor: "#f6e9b5",
                                      borderRadius: "4px",
                                      padding: "1px 4px",
                                      border: "1px solid #918345",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        color: "#929100",
                                        fontSize: "10px",
                                      }}
                                    >
                                      Not saved
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
          <Box className={listOfUsersStyles.actionBox}>
            {/* pagination */}
            <Pagination
              rows={String(itemsPerPage)}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalDataLength={listOfUsers.length}
              dataPerPage={itemsPerPage}
              handleItemsPerPageChange={(e) => handleItemsPerPageChange(e)}
              fieldName="Users"
            />
            {/* action buttons */}
            <Box className={listOfUsersStyles.actionButtonsBox}>
              <Button
                variant="outlined"
                disabled={
                  compareUserLists(
                    initialUserList,
                    clientData.userDetailsList
                  ) || !isUserEdit
                }
                onClick={() => {
                  handleOnSaveAllUsers();
                }}
              >
                Save All
              </Button>
              <Button
                variant="outlined"
                disabled={selectedRow === 0}
                onClick={() => {
                  setActionBoolean(true);
                  setMode(!isUserEdit ? "View" : "Edit");
                }}
              >
                {!isUserEdit ? "View " : "Edit "}
                User
              </Button>
              <Button
                variant="outlined"
                disabled={selectedRow !== 0 || !isUserEdit}
                onClick={() => {
                  setActionBoolean(true);
                  setMode("Create");
                }}
              >
                Create New User
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {actionBoolean && (
        <CreateUser
          index={selectedRow - 1}
          mode={mode}
          handleOnCreateUserCancel={handleOnCreateUserCancel}
          handleOnSuccessBack={handleOnSuccessBack}
        />
      )}
    </>
  );
};

export default ListOfUsers;
